import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { baseColors } from '../../../../assets/theme';
import { ReactComponent as Restore } from '../../../../assets/svg/restore.svg';
import { ReactComponent as Weekly } from '../../../../assets/svg/weeklyCalendar.svg';
import { ReactComponent as Monthly } from '../../../../assets/svg/monthlyCalendar.svg';
import SipForm from './SipForm';
import { MetalType, MetalPriceFields } from './useMetalSip';
import useUserSubscription from '../../../../hooks/useUserSubscription';
import { ClientListWithSearch } from '../../ClientListWithSearchDropdown';

export type SipFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type SipStatus = 'ACTIVE' | 'PAUSED';

type SipOptionsProps = {
  client: any;
  setClient: (client: any) => void;
  fetchClients: boolean;
  selectedFrequency: SipFrequency | null;
  onSelectFrequency: (frequency: SipFrequency | null) => void;
  showClientError: boolean;
  onMissingClient: () => void;
  onClearError: () => void;
  isTermsChecked: boolean;
  onToggleTerms: () => void;
  sipAmount: number;
  onSipAmountChange: (amount: number) => void;
  buyingPrice?: number;
  commission?: number;
  commissionPercentage?: number;
  metalLivePrice?: any;
  metalLivePriceLoading?: boolean;
  isGramSelected: boolean;
  onSwap: () => void;
  selectedGram: number;
  onGramChange: (gram: number) => void;
  gstAmount?: number;
  onGeneratePaymentLink?: (
    authLink?: string,
    errorMessage?: string,
    firstChargeDate?: string,
    scheduleDetails?: { selectedDay?: string; selectedDate?: Date | null },
  ) => void;
  sipStatuses?: Record<SipFrequency, SipStatus | null>;
  existingSipData?: Record<SipFrequency, { amount: number; startDate: string } | null>;
  onEditSip?: (frequency: SipFrequency, newAmount: number) => void;
  onStopSip?: (
    frequency: SipFrequency,
    period?: '7' | '10' | '15' | 'PERMANENT' | '1' | '2' | '3',
  ) => void;
  metalType?: MetalType;
  priceFields?: MetalPriceFields;
  showGold?: boolean;
  showSilver?: boolean;
};

const getSipOptions = (metalType: MetalType) => {
  const metalName = metalType === 'GOLD' ? 'gold' : 'silver';
  return [
    {
      key: 'DAILY' as SipFrequency,
      title: 'Daily SIP',
      description: `Buy ${metalName} daily with as low as ₹10.`,
      icon: <Restore className="text-neutral-800" style={{ width: '24px', height: '24px' }} />,
    },
    {
      key: 'WEEKLY' as SipFrequency,
      title: 'Weekly SIP',
      description: `Buy ${metalName} automatically once a week.`,
      icon: <Weekly className="text-neutral-800" style={{ width: '24px', height: '24px' }} />,
    },
    {
      key: 'MONTHLY' as SipFrequency,
      title: 'Monthly SIP',
      description: `Automate buying ${metalName} once a month.`,
      icon: <Monthly className="text-neutral-800" style={{ width: '24px', height: '24px' }} />,
    },
  ];
};

const SipOptions: React.FC<SipOptionsProps> = ({
  client,
  setClient,
  fetchClients,
  selectedFrequency,
  onSelectFrequency,
  showClientError,
  onMissingClient,
  onClearError,
  isTermsChecked,
  onToggleTerms,
  sipAmount,
  onSipAmountChange,
  buyingPrice,
  commission,
  commissionPercentage,
  metalLivePrice,
  metalLivePriceLoading,
  isGramSelected,
  onSwap,
  selectedGram,
  onGramChange,
  gstAmount,
  onGeneratePaymentLink,
  sipStatuses,
  existingSipData,
  onEditSip,
  onStopSip,
  metalType = 'GOLD',
  priceFields = { buyPrice: 'goldBuyPrice', sellPrice: 'goldSellPrice' },
  showGold,
  showSilver,
}) => {
  // Fetch subscription data to get subscriptionId
  const clientId = client?.clientPublicId;
  const sourceType = metalType === 'GOLD' ? 'GOLD_SAVING' : 'SILVER_SAVING';
  const { data: subscriptionData, refetch: refetchSubscriptions } = useUserSubscription(
    clientId,
    sourceType,
  );

  // Create a map of frequency to subscriptionId for quick lookup
  const frequencyToSubscriptionIdMap = useMemo(() => {
    const map: Record<SipFrequency, string | undefined> = {
      DAILY: undefined,
      WEEKLY: undefined,
      MONTHLY: undefined,
    };

    if (subscriptionData?.subscriptions) {
      subscriptionData.subscriptions.forEach((subscription) => {
        const freq = subscription.subscriptionFrequency as SipFrequency;
        map[freq] = subscription.subscriptionId;
      });
    }

    return map;
  }, [subscriptionData]);

  const handleOptionClick = (frequency: SipFrequency) => {
    if (!client) {
      onMissingClient();
      return;
    }

    // If clicking on an ACTIVE SIP, show the form directly in edit mode
    if (sipStatuses?.[frequency] === 'ACTIVE') {
      onSelectFrequency(frequency);
    } else {
      // For new SIPs or PAUSED, show normal flow
      onSelectFrequency(frequency);
    }
  };

  const isEditMode = (frequency: SipFrequency) => {
    return sipStatuses?.[frequency] === 'ACTIVE';
  };

  const sipOptions = getSipOptions(metalType);
  return (
    <div className="flex flex-col items-stretch py-3 gap-2 w-full">
      <div className="w-full min-w-0">
        <ClientListWithSearch
          client={client}
          setClient={setClient}
          showGold={showGold}
          showSilver={showSilver}
          refetchClients={fetchClients}
          onChange={onClearError}
        />
        {showClientError && (
          <p className="text-xs text-[#E11D48] font-normal mt-2">
            To start the SIP, please select your client first.
          </p>
        )}
      </div>

      {/* Terms Checkbox - Always visible */}
      <div className="flex items-center mt-2 w-full">
        <Checkbox
          checked={isTermsChecked}
          onClick={onToggleTerms}
          sx={{
            color: baseColors.green[500],
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
            },
            paddingRight: '8px',
            marginLeft: '-8px',
            '&.Mui-checked': {
              color: baseColors.green[700],
            },
          }}
        />
        <span className="text-sm text-stone-700 opacity-60 leading-5 width-[20ch]">
          By proceeding you agree to our{' '}
          <a
            href="https://partner.tapinvest.in/terms"
            className="underline underline-offset-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms & Conditions
          </a>
          {/* ,{' '} */}
          {/* <a
            href="https://partner.tapinvest.in/license"
            className="underline underline-offset-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            Licensing Contract
          </a> */}
          {/* ,{' '} */}
          {/* <a
            href="https://partner.tapinvest.in/agreement-to-sell"
            className="underline underline-offset-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            Agreement to Sell
          </a>{' '} */}
          {/* and{' '}
          <a
            href="https://partner.tapinvest.in/risks"
            className="underline underline-offset-4"
            target="_blank"
            rel="noopener noreferrer"
          >
            Risks
          </a>{' '}
          involved. */}{' '}
          involved.
        </span>
      </div>

      {/* Conditional Content: Form or Options List */}
      {selectedFrequency ? (
        <SipForm
          frequency={selectedFrequency}
          amount={sipAmount}
          onAmountChange={onSipAmountChange}
          buyingPrice={buyingPrice}
          commission={commission}
          commissionPercentage={commissionPercentage}
          metalLivePrice={metalLivePrice}
          metalLivePriceLoading={metalLivePriceLoading}
          isGramSelected={isGramSelected}
          onSwap={onSwap}
          selectedGram={selectedGram}
          onGramChange={onGramChange}
          gstAmount={gstAmount}
          onBack={() => onSelectFrequency(null)}
          onGeneratePaymentLink={onGeneratePaymentLink}
          client={client}
          isEditMode={isEditMode(selectedFrequency)}
          existingSipAmount={existingSipData?.[selectedFrequency]?.amount}
          existingSipStartDate={existingSipData?.[selectedFrequency]?.startDate}
          subscriptionId={
            selectedFrequency ? frequencyToSubscriptionIdMap[selectedFrequency] : undefined
          }
          onEdit={(newAmount) => {
            if (onEditSip) {
              onEditSip(selectedFrequency, newAmount);
            }
          }}
          onStop={(period) => {
            if (onStopSip) {
              onStopSip(selectedFrequency, period);
            }
          }}
          metalType={metalType}
          priceFields={priceFields}
          refetchSubscriptions={refetchSubscriptions}
          sipStatus={sipStatuses?.[selectedFrequency] || null}
        />
      ) : (
        <>
          <div className="flex items-center justify-between gap-2.5 w-full mt-8">
            <h3 className="text-[10px] font-semibold tracking-[0.08em] uppercase text-stone-500">
              SIP OPTIONS
            </h3>
            <span
              className="text-[9px] font-semibold text-green-700 tracking-[0.08em] uppercase rounded px-1.5 py-[3px]"
              style={{ backgroundColor: 'rgba(21, 128, 61, 0.12)' }}
            >
              NEW
            </span>
          </div>

          <div
            className="w-full bg-white rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]"
            style={{ border: '0.5px solid #E7E5E4' }}
          >
            {sipOptions.map((option, index) => (
              <div key={option.key} className="px-5">
                <button
                  type="button"
                  onClick={() => handleOptionClick(option.key)}
                  className={clsx(
                    'w-full flex flex-row items-center gap-2 py-4 text-left transition',
                    {
                      'bg-amber-50': selectedFrequency === option.key,
                      'border-b border-black/6': index < sipOptions.length - 1,
                    },
                  )}
                >
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    {option.icon}
                  </div>
                  <div className="flex flex-col items-start gap-[1px] flex-1">
                    <p className="text-sm font-semibold text-neutral-800/80 leading-[150%]">
                      {option.title}
                    </p>
                    <p className="text-xs font-normal text-neutral-500/70 leading-[150%]">
                      {option.description}
                    </p>
                  </div>
                  <div className="flex items-center justify-center flex-shrink-0 gap-2">
                    {sipStatuses?.[option.key] ? (
                      <span
                        className={clsx(
                          'text-[10px] font-semibold tracking-[0.08em] uppercase rounded px-2 py-1',
                          {
                            'text-green-700': sipStatuses[option.key] === 'ACTIVE',
                            'bg-green-50': sipStatuses[option.key] === 'ACTIVE',
                            'text-orange-700': sipStatuses[option.key] === 'PAUSED',
                            'bg-orange-50': sipStatuses[option.key] === 'PAUSED',
                          },
                        )}
                      >
                        {sipStatuses[option.key]}
                      </span>
                    ) : (
                      <div className="w-3 h-6 flex items-center justify-center">
                        <ChevronRightIcon
                          className="text-neutral-900"
                          style={{ width: '12px', height: '15px' }}
                        />
                      </div>
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SipOptions;
